/**
 * Cook something else on that day (#1337).
 *
 * ⚠️ Collapsed by default, and it stays that way. A plan screen that opens with
 * thirty-six alternatives under every dinner is a plan screen that reads as a
 * question rather than an answer — and the whole product is about not being
 * asked what's for dinner.
 *
 * ⛔ THE TICKS SURVIVE, and the copy says so before the tap rather than after.
 * Somebody halfway down an aisle needs to know that changing Thursday will not
 * hand them a fresh set of empty checkboxes. The server reconciles the list
 * instead of rebuilding it; this is where that promise gets made.
 *
 * A day that has already been rated cannot be swapped — the server refuses it,
 * and its reason is shown as written rather than replaced with something
 * vaguer.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type Deck } from "../api/twoPlates";
import { ApiError } from "../lib/api";

export function SwapMeal({
  planId,
  slotId,
  usedMealIds,
}: {
  planId: number;
  slotId: number;
  usedMealIds: number[];
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // Only fetched once somebody opens it. The plan screen is read at breakfast
  // and should not pay for a deck nobody asked for.
  const deck = useQuery<Deck>({
    queryKey: ["deck"],
    queryFn: () => twoPlates.deck(),
    enabled: open,
  });

  const swap = useMutation({
    mutationFn: (mealId: number) => twoPlates.swapSlot(planId, slotId, mealId),
    onSuccess: () => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-muted mt-2 text-xs underline underline-offset-4"
      >
        Cook something else
      </button>
    );
  }

  const used = new Set(usedMealIds);
  const options = (deck.data?.offered ?? []).filter((m) => !used.has(m.mealId));

  return (
    <div className="border-line mt-2 rounded-lg border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-muted text-xs uppercase tracking-wide">Instead, cook</p>
        <button
          onClick={() => setOpen(false)}
          className="text-muted text-xs underline underline-offset-4"
        >
          Cancel
        </button>
      </div>

      {/* Said BEFORE the tap. This is the fear that stops people changing their
          minds, and it is unfounded. */}
      <p className="text-muted mt-1 text-[11px]">
        Your shopping list keeps everything you have already ticked.
      </p>

      {deck.isLoading && <p className="text-muted mt-2 text-sm">Loading…</p>}

      {deck.data && options.length === 0 && (
        <p className="text-muted mt-2 text-sm">
          Nothing else is on offer for this household right now.
        </p>
      )}

      <div className="mt-2 flex max-h-72 flex-col gap-1.5 overflow-y-auto">
        {options.map((m) => (
          <button
            key={m.mealId}
            onClick={() => swap.mutate(m.mealId)}
            disabled={swap.isPending}
            className="border-line rounded-lg border p-2.5 text-left disabled:opacity-40"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">
                {m.starred && <span aria-label="starred">★ </span>}
                {m.name}
              </span>
              <span className="text-muted shrink-0 text-[11px]">{m.activeMinutes} min</span>
            </div>
            <p className="text-muted mt-0.5 text-xs">
              {m.anchor}
              {m.sides.length > 0 && <> · {m.sides.join(", ")}</>}
            </p>
          </button>
        ))}
      </div>

      {swap.isError && (
        <p role="alert" className="text-warn mt-2 text-sm">
          {/* The server's reason, as written. "That dinner has already been
              rated" tells somebody what to do; "could not swap" does not. */}
          {swap.error instanceof ApiError ? swap.error.message : "That did not work. Try again."}
        </p>
      )}
    </div>
  );
}
